import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { PlusCircle, Edit, Shield, Users as UsersIcon } from "react-feather";
import DataTable from "../../../../components/common/DataTable";
import { useGetCustomersQuery } from "../customersApi";
import { useCanCreateCustomers } from "../useCustomerPermissions";
import { useBranchFilter } from "../../../branchFilter/useBranchFilter";
import { paths } from "../../../../routes/paths";

const STATUS_OPTIONS = [
  { value: "active", label: "Active" },
  { value: "inactive", label: "Inactive" },
  { value: "", label: "All Statuses" },
];

// Landing page for the customer master (texas_master/GUI/Master/Frm_Customer.cs),
// now against the live /api/v1/masters/customers endpoint (handoff §9.7).
// The underlying table has 5000+ rows across branches, so this always
// fetches with a search/isActive filter rather than everything unfiltered -
// defaults to "Active" (~3800 rows) rather than "All" (~5200).
// There is no delete endpoint for customers (by design, like Users).
const CustomersList = () => {
  const canCreate = useCanCreateCustomers();
  const navigate = useNavigate();
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("active");
  const { selectedBranchId } = useBranchFilter();

  useEffect(() => {
    const handle = setTimeout(() => setSearch(searchInput.trim()), 400);
    return () => clearTimeout(handle);
  }, [searchInput]);

  const { data: customers = [], isLoading } = useGetCustomersQuery({
    search: search || undefined,
    isActive: status === "" ? undefined : status === "active",
    branchId: selectedBranchId ?? undefined,
  });

  const columns = [
    {
      title: "Code",
      dataIndex: "custCode",
      width: 90,
      render: (value, record) => (
        <Link to={paths.customerEdit(record.branchId, record.custId)}>{value}</Link>
      ),
    },
    {
      title: "Name",
      dataIndex: "custName",
      width: 280,
      ellipsis: { showTitle: true },
      render: (value, record) => (
        <>
          {value || "-"}
          {record.isMapped && (
            <span className="badge bg-info-transparent ms-2" title={`Home branch: ${record.branchCode}`}>
              Shared from {record.branchCode}
            </span>
          )}
        </>
      ),
    },
    { title: "Branch", dataIndex: "branchCode" },
    {
      title: "Status",
      dataIndex: "isActive",
      render: (value) => (
        <span className={`badge ${value ? "badge-linesuccess" : "badge-linedanger"}`}>
          {value ? "Active" : "Inactive"}
        </span>
      ),
    },
    {
      title: "Actions",
      dataIndex: "actions",
      render: (_, record) => (
        <div className="edit-delete-action">
          <button
            type="button"
            className="btn btn-link me-1 p-2"
            title="Edit"
            onClick={() => navigate(paths.customerEdit(record.branchId, record.custId))}
          >
            <Edit size={16} />
          </button>
          <button
            type="button"
            className="btn btn-link me-1 p-2"
            title="Guarding Assignments"
            onClick={() => navigate(paths.customerGuarding(record.branchId, record.custId))}
          >
            <Shield size={16} />
          </button>
          <button
            type="button"
            className="btn btn-link p-2"
            title="Bulk Entry"
            onClick={() => navigate(paths.customerBulkGuards(record.branchId, record.custId))}
          >
            <UsersIcon size={16} />
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="page-header">
          <div className="add-item d-flex">
            <div className="page-title">
              <h4>Customers</h4>
              <h6>Customer master and site guarding assignments</h6>
            </div>
          </div>
          {canCreate && (
            <div className="page-btn">
              <button type="button" className="btn btn-added" onClick={() => navigate(paths.customerNew)}>
                <PlusCircle className="me-2" size={16} />
                Add New Customer
              </button>
            </div>
          )}
        </div>

        <div className="card table-list-card">
          <div className="card-body">
            <div className="table-top">
              <div className="search-set">
                <div className="search-input">
                  <input
                    type="text"
                    placeholder="Search customer name"
                    className="form-control form-control-sm formsearch"
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                  />
                </div>
              </div>
              <div className="form-sort">
                <select className="form-control" value={status} onChange={(e) => setStatus(e.target.value)}>
                  {STATUS_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="table-responsive">
              <DataTable
                columns={columns}
                dataSource={customers}
                rowKey={(record) => `${record.branchId}-${record.custId}`}
                loading={isLoading}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomersList;
